import { useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ReactInputMask from 'react-input-mask';
import { Button } from '@shared/ui/button';
import { contactPersonSchema } from '../lib/contact-person-schema';
import { InputField } from '@shared/ui/input-field';
import { LoadingWrapper } from '@shared/ui/loading-wrapper';
import { FormCard } from '@shared/ui/form-card';
import {
  createContact,
  fetchContactById,
  updateContact,
  resetCurrentContact,
  selectCurrentContact,
} from '../../../entities/contact/model';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import { useMe } from '../../../pages/Area/Area';
import { AbilityContext } from '@shared/lib/ability';

interface IDefaultValues {
  id: string;
  name: string;
  surname: string;
  patronymic: string;
  position: string;
  personal_phone: string;
  integrator: string;
  main: boolean;
  cart: string;
  personality: string;
}

const CompaniesContactPersonAddEdit = () => {
  const { me } = useMe();
  const { contactPersonId } = useParams();
  const contact = useAppSelector(selectCurrentContact);
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) => state.contacts.status);
  const ability = useContext(AbilityContext);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    reset,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useForm<IDefaultValues | any>({
    mode: 'onBlur',
    resolver: yupResolver(contactPersonSchema),
    shouldUnregister: false,
    defaultValues: {
      ...contact,
    },
  });

  useEffect(() => {
    if (status === 'idle' && contactPersonId) {
      dispatch(fetchContactById(contactPersonId));
    }
  }, [status, dispatch, contactPersonId]);

  useEffect(() => {
    reset({ ...contact });
  }, [reset, contact]);

  const onSubmit = async (data: IDefaultValues) => {
    if (ability.can('change', 'contact') && contactPersonId) {
      dispatch(
        updateContact({
          ...data,
          company: me?.company?.id,
          id: contactPersonId,
        })
      ).then(() => {
        dispatch(resetCurrentContact());
      });
    }
    if (ability.can('add', 'contact') && !contactPersonId) {
      dispatch(createContact({ ...data, company: me.company?.id })).then(() => {
        dispatch(resetCurrentContact());
      });
    }
    navigate(-1);
  };

  return (
    <LoadingWrapper type="main" status={status}>
      <FormCard
        title={contactPersonId ? 'Редактирование контакта' : 'Новый контакт'}
        description="Контактное лицо в компании, для связи менеджера с человеком на стороне клиента"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
          {/* ФИО */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
            <InputField
              id="surname"
              inputName="surname"
              label="Фамилия"
              errors={errors}
              register={register}
            />
            <InputField
              id="name"
              inputName="name"
              label="Имя"
              errors={errors}
              register={register}
            />
            <InputField
              id="patronymic"
              inputName="patronymic"
              label="Отчество"
              errors={errors}
              register={register}
            />
          </div>

          {/* Должность + телефон */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <InputField
              id="position"
              inputName="position"
              label="Должность"
              errors={errors}
              register={register}
            />
            <Controller
              name="personal_phone"
              control={control}
              render={({ field }) => (
                <InputField
                  {...field}
                  as={ReactInputMask}
                  mask="+7 (***) ***-**-**"
                  id="personal_phone"
                  inputName="personal_phone"
                  label="Телефон"
                  errors={errors}
                  register={register}
                />
              )}
            />
          </div>

          <div className="pt-4 flex gap-2">
            <Button
              type="submit"
              disabled={!isValid || (Boolean(contactPersonId) && !isDirty)}
            >
              {contactPersonId ? 'Сохранить' : 'Добавить'}
            </Button>
            <Button isWhite onClick={() => navigate(-1)}>
              Отмена
            </Button>
          </div>
        </form>
      </FormCard>
    </LoadingWrapper>
  );
};

export default CompaniesContactPersonAddEdit;
